/**
 * Componente para indicador visual del estado del SLA (verde/amarillo/rojo)
 * 
 * Referencia: sistema-tickets-frontend-COMPLETO.md Sección 2.3
 */

import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import dayjs from 'dayjs';

/**
 * Componente SLAIndicator
 * 
 * @param {Object} props
 * @param {Object} props.ticket - Objeto completo del ticket
 * @param {string} props.ticket.status - Estado del ticket
 * @param {boolean} props.ticket.firstResponseViolated - Si la primera respuesta violó el SLA
 * @param {boolean} props.ticket.resolutionViolated - Si la resolución violó el SLA
 * @param {string|null} props.ticket.firstResponseAt - Fecha de primera respuesta
 * @param {string|null} props.ticket.firstResponseDueAt - Fecha límite para primera respuesta
 * @param {string|null} props.ticket.resolutionDueAt - Fecha límite para resolución
 * @param {Object} props.ticket.prioridad - Objeto de prioridad con firstResponseHours y resolutionHours
 * @returns {JSX.Element}
 */
const SLAIndicator = ({ ticket }) => {
  // Ticket cerrado o resuelto → No mostrar
  if (['cerrado', 'resuelto'].includes(ticket.status)) {
    return <Chip label="N/A" size="small" />;
  }
  
  // Verificar si algún SLA fue violado
  if (ticket.firstResponseViolated || ticket.resolutionViolated) {
    return (
      <Tooltip title="SLA violado">
        <Chip
          icon={<ErrorIcon />}
          label="Vencido"
          size="small"
          color="error"
        />
      </Tooltip>
    );
  }
  
  // Calcular tiempo restante hasta vencimiento
  const ahora = dayjs();
  
  // Determinar qué fecha de vencimiento usar
  // Si ya se dio primera respuesta, usar resolutionDueAt
  // Si no, usar firstResponseDueAt
  const vencimiento = ticket.firstResponseAt
    ? dayjs(ticket.resolutionDueAt)
    : dayjs(ticket.firstResponseDueAt);
  
  if (!vencimiento.isValid()) {
    return <Chip label="N/A" size="small" />;
  }
  
  const horasRestantes = vencimiento.diff(ahora, 'hour', true);
  
  // Si ya pasó la fecha de vencimiento (pero no está marcado como violado)
  if (horasRestantes < 0) {
    return (
      <Tooltip title="SLA vencido">
        <Chip
          icon={<ErrorIcon />}
          label="Vencido"
          size="small"
          color="error"
        />
      </Tooltip>
    );
  }
  
  // Rojo si faltan menos de 2 horas
  if (horasRestantes < 2) {
    const minutosRestantes = Math.round(horasRestantes * 60);
    return (
      <Tooltip title={`Vence en ${minutosRestantes} minutos`}>
        <Chip
          icon={<WarningIcon />}
          label="Crítico"
          size="small"
          sx={{ bgcolor: '#ffebee', color: '#c62828' }}
        />
      </Tooltip>
    );
  }
  
  // Amarillo si faltan menos del 25% del tiempo
  const tiempoTotal = ticket.firstResponseAt
    ? ticket.prioridad?.resolutionHours || 72
    : ticket.prioridad?.firstResponseHours || 8;
  
  const porcentajeRestante = (horasRestantes / tiempoTotal) * 100;
  
  if (porcentajeRestante < 25) {
    return (
      <Tooltip title={`Vence en ${Math.round(horasRestantes)} horas`}>
        <Chip
          icon={<WarningIcon />}
          label="Próximo"
          size="small"
          sx={{ bgcolor: '#fff3e0', color: '#e65100' }}
        />
      </Tooltip>
    );
  }
  
  // Verde si hay tiempo suficiente
  return (
    <Tooltip title={`Vence en ${Math.round(horasRestantes)} horas`}>
      <Chip
        icon={<CheckCircleIcon />}
        label="OK"
        size="small"
        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }}
      />
    </Tooltip>
  );
};

export default SLAIndicator;

